import { useEffect } from 'react';

/**
 * Tints the browser's own chrome (iOS Safari's top/bottom bars, Chrome's status bar) to match a
 * full-screen scrim while one is open.
 *
 * The scrim covers the PAGE, but the browser's toolbars are not part of the page — so dimming the
 * content leaves the bars at the document's resting colour and the dim stops dead at the viewport
 * edge. `theme-color` is what governs that chrome, and ours is declared statically in index.html
 * (one light, one dark, keyed to prefers-color-scheme), so it never learned about the scrim.
 *
 * Each meta is dimmed against ITS OWN current value rather than against some separately-derived
 * page colour. That is correct by construction: whatever the tag currently says is exactly what
 * the toolbar is currently showing, so compositing the scrim over it produces what the toolbar
 * would show if the scrim extended into it. It also keeps light and dark independently right
 * without this having to know which scheme is active.
 *
 * The compositing is done by painting both colours onto a 1x1 canvas and reading the pixel back,
 * rather than by parsing and blending the channels here. The palette is authored in `oklch`, and
 * `getComputedStyle` hands those back AS `oklch(...)` — an rgb-shaped parser silently matches
 * nothing and the whole hook becomes a no-op. Letting the canvas do it means any colour syntax the
 * browser accepts works, and the alpha blend is the browser's own.
 *
 * Reference-counted, because a modal can open on top of the loading mask: the colour is applied on
 * the first scrim and only restored when the last one goes away, so a nested close does not leave
 * the bars stuck dark.
 *
 * TWO MECHANISMS, because the browsers disagree about what colours their chrome:
 *
 *   - `theme-color` — Chrome honours it, and iOS Safari did through version 18.
 *   - the BODY's background-color — what Safari 26 reads, having dropped `theme-color` (it still
 *     parses the tag, then ignores it). Its fallback order is body, then <html>, then plain
 *     white/black.
 *
 * Both are set together, since a single build serves every browser and neither can be detected
 * cheaply or reliably. Writing both is harmless: each browser reads the one it cares about.
 */

/*
 * Module-level so the count is shared across every scrim on screen rather than per-component.
 * fp/no-let and fp/no-mutation are off through this file for that reason: reference counting and
 * writing `meta.content` are mutation by nature, with no functional equivalent for "tell the
 * browser what colour its toolbar is".
 */
/* eslint-disable @silverhand/fp/no-let, @silverhand/fp/no-mutation */
let scrimCount = 0;
let savedContents: string[] | undefined;
/** The body's own inline background, so it can be handed back exactly as it was. */
let savedBodyBackground: string | undefined;
/** Set once the document is leaving, so the teardown does not undo the tint mid-redirect. */
let isUnloading = false;

/** Paint `overlay` over `base` and read back the flattened result — `theme-color` cannot carry alpha. */
const compositeOver = (base: string, overlay: string): string | undefined => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return;
  }

  context.fillStyle = base;
  context.fillRect(0, 0, 1, 1);
  context.fillStyle = overlay;
  context.fillRect(0, 0, 1, 1);

  const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;

  if (red === undefined || green === undefined || blue === undefined) {
    return;
  }

  return `#${[red, green, blue].map((value) => value.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Call from any component that renders a full-screen scrim.
 *
 * `isActive` exists because a modal stays MOUNTED with its `isOpen` toggling — keying off mount
 * alone would tint the chrome for a closed dialog and never let go. Components that only exist
 * while their scrim is on screen (the loading mask) can leave it defaulted.
 */
const useScrimThemeColor = (isActive = true) => {
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const metas = [...document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')];

    if (metas.length === 0) {
      return;
    }

    scrimCount += 1;

    if (scrimCount === 1) {
      const mask = getComputedStyle(document.documentElement).getPropertyValue('--backdrop').trim();

      if (mask) {
        savedContents = metas.map((meta) => meta.content);

        for (const meta of metas) {
          // A meta whose colour the canvas cannot parse keeps its original value rather than
          // being blanked — a stale tint is a far smaller problem than an empty theme-color.
          meta.content = compositeOver(meta.content, mask) ?? meta.content;
        }

        /*
         * Safari 26 dropped `theme-color` — it still parses the tag and then ignores it — and
         * derives the toolbar tint from the BODY's background-color instead (falling back to
         * <html>, then to white/black). So the meta above covers Chrome and iOS <= 18, and this
         * covers Safari 26.
         *
         * Repainting the body is invisible on screen: the scrim is `position: fixed; inset: 0`
         * and already covers the body completely, so the only thing this changes is the colour
         * Safari reads. That is why it is preferable to the other documented workaround —
         * mounting sentinel elements at the viewport edges purely to be sampled — which adds
         * real DOM for a purely informational purpose.
         */
        const { body } = document;
        savedBodyBackground = body.style.backgroundColor;
        const bodyTint = compositeOver(getComputedStyle(body).backgroundColor, mask);

        if (bodyTint) {
          body.style.backgroundColor = bodyTint;
        }
      }
    }

    const restore = () => {
      if (savedBodyBackground !== undefined) {
        document.body.style.backgroundColor = savedBodyBackground;
        savedBodyBackground = undefined;
      }

      if (!savedContents) {
        return;
      }

      for (const [index, meta] of metas.entries()) {
        const saved = savedContents[index];

        if (saved !== undefined) {
          meta.content = saved;
        }
      }

      savedContents = undefined;
    };

    /*
     * A scrim very often precedes leaving the page entirely — the loading mask goes up and an
     * OAuth redirect follows. React tears the mask down as part of that, and restoring the
     * resting colour at that moment makes the bars flash back to light for the gap between
     * this document unloading and the provider's page painting. Suppress the restore once the
     * page is on its way out; the next document brings its own `theme-color` anyway.
     *
     * Trade-off: if a navigation starts and is then cancelled, no `pageshow` follows, so this
     * document keeps the dimmed tint until the next navigation. That is deliberately preferred
     * over flashing the bars on every redirect, which is the common path.
     */
    const onPageHide = () => {
      isUnloading = true;
    };

    /*
     * ...except on a back-navigation out of the bfcache, where the SAME document is resurrected
     * with our dimmed value still written into the tag and no scrim on screen to justify it.
     * `persisted` is what distinguishes that from an ordinary load.
     */
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        isUnloading = false;
        restore();
      }
    };

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);

      scrimCount -= 1;

      if (scrimCount === 0 && !isUnloading) {
        restore();
      }
    };
  }, [isActive]);
};

export default useScrimThemeColor;
/* eslint-enable @silverhand/fp/no-let, @silverhand/fp/no-mutation */
